<template>
    <popper
        ref="popper"
        :trigger="trigger"
        :disabled="options.length === 0"
        :force-show="forceShow"
        :options="{
            placement: placement,
            modifiers: { 
                offset: {
                    offset: '0, 5'
                },
                flip: {
                    behavior: ['bottom', 'top', 'bottom']
                },
                preventOverflow: {
                    escapeWithReference: true
                }
            },
            multiple: multiple

        }"
        transition="fade"
        enter-active-class="fade-enter fade-enter-active"
        leave-active-class="fade-leave fade-leave-active"
        append-to-body
        is-visible
        @show="onShow"
        @hide="onHide">
        <div :class="align ? 'text-' + align : 'text-left'" class="popper">
            <div
                ref="select-menu"
                class="select-menu">
                <ul>
                    <app-option key="NO_RESULTS" 
                                :show="noResults" 
                                :value="null" 
                                label="NO_RESULTS" />
                    <slot/>
                </ul>
            </div>
        </div>

        <span slot="reference">
            <app-input 
                v-if="!multiple"
                :is-open="isOpen"
                :select-input="true"
                :filterable="filterable"
                :value="selectedLabel"
                :placeholder="placeholder"
                :size="size"
                @blur="handleBlur"
                @filter="handleFilter"
                @focus="handleFocus"
                @click="handleClick"
                @enter="handleEnter"
                @up="handleUp"
                @down="handleDown"
                @mouseenter.native="isHovering = true"
                @mouseleave.native="isHovering = false">
                <template slot="append">
                    <i v-show="!showClose" class="icon-dropdown" />
                    <i v-if="showClose" class="icon-close" @click="handleClearClick" />
                </template>
            </app-input>
            <app-select-input
                v-if="multiple"
                ref="input"
                :is-open="isOpen"
                :placeholder="placeholder"
                :selected="selected"
                :use-accessibility="useAccessibility"
                @blur="handleBlur"
                @focus="handleFocus"
                @click="handleClick"
                @removeOption="handleSelectOption"
                @enter="handleEnter"
                @up="handleUp"
                @down="handleDown"/>
        </span>
    </popper>
</template>

<script>
import Popper from 'vue-popperjs'
import Vue from 'vue'
import emitter from '@/utilities/emitter'
import { setTimeout } from 'timers'

export default {
    name: 'app-select',
    components: {
        'popper': Popper
    },
    mixins: [emitter],
    provide() {
        return {
            'AppSelect': this
        };
    },
    props: {
        trigger: {
            type: String,
            default: 'click'
        },
        placeholder: String,
        placement: {
            type: String,
            default: 'bottom-start'
        },
        multiple: {
            type: Boolean,
            default: false
        },
        align: {
            type: String,
            default: 'left'
        },
        filterable: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: false
        },
        value: {}
    },
    data() {
        return {
            cachedOptions: [],
            options: [],
            vueOptions: [],
            optionsCount: 0,
            filteredOptionsCount: 0,
            hoveredIndex: 0,
            useArrowButton: false,
            selected: this.multiple ? [] : {},
            selectedLabel: String,
            isOpen: false,
            isHovering: false,
            closePopper: false,
            forceShow: false,
            closeOnBlur: true,
            useAccessibility: false,
            size: this.$attrs.size,
            mousedownOverride: false
        }
    },
    computed: {
        noResults() {
            return this.options.length === 1
        },
        hoveredOption() {
            return this.options[this.hoveredIndex]
        },
        showClose() {
            let hasValue = this.multiple
                ? Array.isArray(this.value) && this.value.length > 0
                : this.value !== undefined && this.value !== null && this.value !== ''
            let criteria = this.clearable &&
                this.isHovering &&
                hasValue
            return criteria
        }
    },
    created() {
        // Temporarily sets the value before we get the labels from the DB
        if (!this.multiple)
            this.selectedLabel = this.value
        else {
            this.value.forEach(value => {
                this.selected.push({'label': value, 'value': value})
            })
        }
    },
    methods: {
        handleSelectOption(option) {
            if (!this.multiple) {
                this.$emit('input', option.value)
                this.emitChange(option.value)
            }
            else {
                this.multipleSelected(option)
            }
        },
        handleClick(bool) {
            setTimeout(() => {
                if (!this.mousedownOverride)
                    this.isOpen = bool
            }, 100)
        },
        handleFilter(value) {
            this.isOpen = true
            this.options = this.cachedOptions.filter(el=> el.label.toLowerCase().trim().indexOf(value.toLowerCase().trim()) > -1)
            this.options.push({value: '', label: 'NO_RESULTS'})
        },
        emitChange(val) {
            if (!this.valueEquals(this.value, val)) {
                this.$emit('change', val);
            }
        },
        multipleSelected(option) {
            let index = this.selected.map(el => el.value).indexOf(option.value);
            if (index > -1)
                this.selected.splice(index, 1)
            else
                this.selected.push(option)
            let selectedArray = this.selected.map((el) => {
                return el.value
            })
            this.setHoveredIndex(option)
            this.$emit('input', selectedArray)
            this.emitChange(selectedArray)
        },
        resetSelected() {
            if (this.value && Array.isArray(this.value)) {
                this.selected = []
                let temp = []
                this.value.forEach((val) => {
                    temp.push(this.getOption(val))
                })
                temp.forEach((option) => {
                    this.multipleSelected(option)
                })
            }
        },
        setSelected() {
            let option = this.getOption(this.value)
            if (!this.multiple && option) {
                this.selectedLabel = option.label && option.label !== 'NO_RESULTS' ? option.label : ''
                this.selected = option
                this.forceShow = false
                return
            }
        },
        setHoveredIndex(option) {
            this.hoveredIndex = this.options.map(el => el.value).indexOf(option.value)
        },
        getOption(value) {
            let option;
            option = this.cachedOptions.filter( (el)=> {
                return el.value === value
            })
            return option[0]
        },
        valueEquals(a,b) {
            if (a === b) return true;
            if (!(a instanceof Array)) return false
            if (!(b instanceof Array)) return false
            if (a.length !== b.length) return false
            for (let i = 0; i !== a.length; ++i) {
                if (a[i] !== b[i]) return false
            }
            return true
        },
        onShow() {
            this.isOpen = true
        },
        onHide() {
            this.isOpen = false
        },
        handleFocus(evt) {
            if (this.filterable)
                evt.target.select()
            if (!this.isOpen)
                this.forceShow = true
        },
        handleBlur() {
            if (this.isOpen && this.closeOnBlur)
                this.forceShow = false
            this.$nextTick(() => this.closeOnBlur = true)
        },
        handleEnter() {
            if (this.hoveredOption && this.options.length < this.optionsCount &&  this.hoveredIndex >= 0) {
                this.handleSelectOption(this.hoveredOption)
            }
            else if (this.hoveredOption && this.hoveredIndex > 0) {
                this.handleSelectOption(this.hoveredOption)
            }
            else
                this.handleClick(!this.isOpen)
        },
        handleUp() {
            if (this.isOpen) {
                this.useAccessibility = true
                if (this.options.length < this.optionsCount && this.hoveredIndex <= 0)
                    this.hoveredIndex = this.options.length-2
                else if (this.options.length === this.optionsCount && this.hoveredIndex <= 1)
                    this.hoveredIndex = this.options.length-1
                else
                    this.hoveredIndex--
                this.$nextTick(() => this.scrollIntoView(this.hoveredOption.value))
            }
        },
        handleDown() {
            if (!this.isOpen)
                this.$nextTick(() => this.handleClick(true))
            else {
                this.useAccessibility = true
                if (this.options.length < this.optionsCount && this.options.length-2 <= this.hoveredIndex)
                    this.hoveredIndex = 0
                else if (this.options.length === this.optionsCount && this.options.length-1 <= this.hoveredIndex)
                    this.hoveredIndex = 1
                else
                    this.hoveredIndex++
                this.$nextTick(() => this.scrollIntoView(this.hoveredOption.value))
            }
        },
        handleClearClick(event) {
            event.stopPropagation()
            const value = this.multiple ? [] : null
            this.$emit('input', value)
            this.emitChange(value)
            this.visible = false
            this.$emit('clear')
        },
        scrollIntoView(value) {
            if (!this.$refs) return
            
            const container = this.$refs['select-menu']
            let selected = null
            if (value !== '' && value !== null) {
                selected = this.vueOptions.filter(el => { return el.$refs[value]})[0].$refs[value]
            }
            if (Vue.prototype.$isServer) return;

            if (!selected) {
                container.scrollTop = 0;
                return;
            }

            const offsetParents = [];
            let pointer = selected.offsetParent;
            while (pointer && container !== pointer && container.contains(pointer)) {
                offsetParents.push(pointer)
                pointer = pointer.offsetParent
            }
            const top = selected.offsetTop + offsetParents.reduce((prev, curr) => (prev + curr.offsetTop), 0)
            const bottom = top + selected.offsetHeight
            const viewRectTop = container.scrollTop
            const viewRectBottom = viewRectTop + container.clientHeight

            if (top < viewRectTop) {
                container.scrollTop = top
            }
            else if (bottom > viewRectBottom) {
                container.scrollTop = bottom - container.clientHeight
            }
        }
    },
    watch: {
        isOpen(){
            this.forceShow = this.isOpen
            this.$nextTick(() => {
                if (!this.isOpen) {
                    this.options = this.cachedOptions
                    this.closePopper = false
                    this.useAccessibility = false
                    this.hoveredIndex = 0
                }
                else {
                    if (!this.multiple) {
                        setTimeout(() => { this.scrollIntoView(this.value) }, 50)
                    }
                }
            })
        },
        value() {
            this.setSelected()
            this.dispatch('AppFormItem', 'app.form.change', [this.value])
            if (this.value && this.value.length !== this.selected.length) {
                this.resetSelected()
            }
            this.$nextTick( ()=> {
                this.$children[0].updatePopper()
            })
        },
        cachedOptions() {
            if (!this.multiple)
                this.setSelected(this.value)
            else {
                if (this.cachedOptions.length > 1)
                    this.resetSelected()
            }
        }
    }
}
</script>

<style lang="stylus">
    .fade-enter-active,
    .fade-leave-active
        transition: opacity .3s

    .fade-enter-to .popper
        transition: .3s
        opacity: 1

    .fade-leave-to .popper
        transition: .3s
        opacity: 0
    
    .fade-enter .popper
        opacity: 0
    
    .popper
        width: 235px
        text-align: left
        padding: 0px
        display: inline-block
        position: absolute
        font-size: 12px
        font-weight: normal
        z-index: 200000
        border: 1px solid #E4E7ED
        border-radius: 6px
        background-color: white
        -webkit-box-shadow: 0 2px 12px 0 rgba(0,0,0,.1)
        box-shadow: 0 2px 12px 0 rgba(0,0,0,.1)
        -webkit-box-sizing: border-box
        box-sizing: border-box 
        margin: 0px

        span 
            display: block
    
    .select-menu
        background: white
        max-height: 274px
        overflow: scroll
        border: 0px solid #E4E7ED
        border-radius: 6px
        ul
            margin: 0px
            padding: 6px 0px
            list-style: none
        li
            color: #606266
            cursor: pointer,
            &.hover,
            &:hover
                background-color: #0d5bdb
                color: white
    
    // Popper Arrow Styles
    .popper .popper__arrow
        width: 0
        height: 0
        border-style: solid
        border-color: white
        position: absolute
        margin: 20px

    .popper[x-placement^="top"]
        margin-bottom: 10px

    .popper[x-placement^="top"] .popper__arrow
        border-width: 5px 5px 0 5px
        border-color: #FFF transparent transparent transparent
        bottom: -5px
        left: 15px !important
        margin-top: 0
        margin-bottom: 0

    .popper[x-placement^="bottom"]
        margin-top: 20px

    .popper[x-placement^="bottom"] .popper__arrow
        border-width: 0 5px 5px 5px
        border-color: transparent transparent #FFF transparent
        top: -5px
        left: 15px !important
        margin-top: 0
        margin-bottom: 0
</style>

